import { supabase } from './supabaseClient';

/**
 * PARTNER STORAGE HELPER - SECURE ROLE-BASED UPLOADS
 * =====================================================
 * WORKAROUND: Uses temp_image_uploads table instead of storage.objects
 * to bypass RLS policy issues blocking file uploads
 * 
 * SUPPORTS: Both partner authentication (localStorage) and admin authentication (Supabase session)
 */

export interface UploadResult {
  success: boolean;
  url?: string;
  path?: string;
  error?: string;
  debugInfo?: {
    fileSize: number;
    fileType: string;
    fileName: string;
    uploadDuration: number;
    authStatus?: AuthDebugInfo;
  };
}

export interface AuthDebugInfo {
  hasSession: boolean;
  userId: string | null;
  role: string | null;
  isAuthenticated: boolean;
  isPartner: boolean;
  isAdmin: boolean;
  partnerId: string | null;
  errorMessage?: string;
}

/**
 * Check if user is authenticated (admin or partner)
 */
export async function checkPartnerAuth(): Promise<boolean> {
  try {
    // Simply check if user is logged in - no role verification
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error('Auth check error:', error);
      return false;
    }

    // Return true if any session exists (admin or partner)
    return !!session;
  } catch (error) {
    console.error('Authentication check failed:', error);
    return false;
  }
}

/**
 * Upload offer image to Supabase storage
 * COMPLETELY PUBLIC UPLOAD - No authentication required
 * Upload field is only accessible on protected admin pages
 */
export async function uploadOfferImage(
  file: File,
  offerId?: string
): Promise<{ url: string; path: string } | null> {
  try {
    console.log('🚀 PUBLIC UPLOAD - No auth checks, direct upload to storage');

    // Generate unique filename without requiring user ID
    const fileExt = file.name.split('.').pop();
    const fileName = offerId 
      ? `${offerId}_${Date.now()}.${fileExt}`
      : `public_${Date.now()}.${fileExt}`;
    const filePath = `offer-images/${fileName}`;

    console.log('📤 Uploading image:', {
      fileName,
      filePath,
      fileSize: file.size,
      fileType: file.type
    });

    // CRITICAL FIX: Upload file to offer-images bucket (PUBLIC bucket)
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('offer-images')  // ✅ FIXED: Changed from 'partner-offer-images' to 'offer-images'
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (uploadError) {
      console.error('❌ Upload error:', uploadError);
      throw new Error(`Failed to upload image: ${uploadError.message}`);
    }

    console.log('✅ Upload successful:', uploadData);

    // Get public URL (since this is a public bucket)
    const { data: urlData } = supabase.storage
      .from('offer-images')  // ✅ FIXED: Changed from 'partner-offer-images' to 'offer-images'
      .getPublicUrl(filePath);

    if (!urlData?.publicUrl) {
      throw new Error('Failed to get public URL for uploaded image');
    }

    console.log('🌐 Public URL generated:', urlData.publicUrl);

    return {
      url: urlData.publicUrl,
      path: filePath
    };
  } catch (error: any) {
    console.error('❌ Image upload failed:', error);
    throw error;
  }
}

/**
 * WORKAROUND: Convert file to base64 and store in temp_image_uploads table
 * This bypasses storage.objects RLS issues entirely
 */
const convertFileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

/**
 * WORKAROUND: Upload image by storing base64 data in temp_image_uploads table
 * 
 * @param file - File to upload
 * @param offerId - Offer ID (optional for now)
 * @param uploadType - 'header-image', 'business-logo', or 'barcode'
 * @returns UploadResult with data URL or error
 */
export const uploadOfferImageLegacy = async (
  file: File,
  offerId: string | null,
  uploadType: 'header-image' | 'business-logo' | 'barcode'
): Promise<UploadResult> => {
  const startTime = Date.now();
  
  try {
    // STEP 1: Validate file
    if (!file) {
      return {
        success: false,
        error: 'No file provided'
      };
    }
    
    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      return {
        success: false,
        error: `File size (${(file.size / 1024 / 1024).toFixed(2)}MB) exceeds 10MB limit`
      };
    }
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      return {
        success: false,
        error: `Invalid file type: ${file.type}. Allowed: JPEG, PNG, WebP, GIF`
      };
    }

    // STEP 2: Convert file to base64
    console.log('📦 Converting file to base64...');
    const base64Data = await convertFileToBase64(file);
    
    // STEP 3: Store in temp_image_uploads table (bypasses storage.objects RLS)
    console.log('💾 Storing image data in temp_image_uploads table...');
    
    // For admins, use the selected partner ID if available (from context or query param)
    // For partners, use their own partner ID
    const partnerIdToUse = offerId || null;
    
    const { data: uploadData, error: uploadError } = await supabase
      .from('temp_image_uploads')
      .insert({
        partner_id: partnerIdToUse,
        offer_id: offerId,
        upload_type: uploadType,
        file_name: file.name,
        file_type: file.type,
        file_size: file.size,
        image_data: base64Data
      })
      .select()
      .single();
    
    if (uploadError) {
      console.error('❌ Database insert failed:', uploadError);
      return {
        success: false,
        error: `Upload failed: ${uploadError.message}`,
        debugInfo: {
          fileSize: file.size,
          fileType: file.type,
          fileName: file.name,
          uploadDuration: Date.now() - startTime
        }
      };
    }
    
    console.log('✅ Upload successful - stored as base64 in database');
    
    // Return the base64 data URL directly (can be used in <img> src)
    return {
      success: true,
      url: base64Data, // Return base64 data URL for immediate use
      path: uploadData.id, // Store upload ID as path reference
      debugInfo: {
        fileSize: file.size,
        fileType: file.type,
        fileName: file.name,
        uploadDuration: Date.now() - startTime
      }
    };
  } catch (err: any) {
    console.error('❌ Upload exception:', err);
    return {
      success: false,
      error: `Upload failed: ${err.message}`,
      debugInfo: {
        fileSize: file.size,
        fileType: file.type,
        fileName: file.name,
        uploadDuration: Date.now() - startTime
      }
    };
  }
};

/**
 * WORKAROUND: Upload barcode with base64 storage
 */
export const uploadOfferBarcode = async (
  file: File,
  offerId: string | null
): Promise<UploadResult> => {
  return uploadOfferImageLegacy(file, offerId, 'barcode');
};

/**
 * Update offer with image URLs after successful upload
 * 
 * @param offerId - Offer ID to update
 * @param imageUrls - Object containing image URLs (base64 data URLs)
 * @returns Success status and error message if any
 */
export const updateOfferImageUrls = async (
  offerId: string,
  imageUrls: {
    imageUrl?: string;
    businessLogoUrl?: string;
    barcodeUrl?: string;
  }
): Promise<{ success: boolean; error?: string }> => {
  try {
    if (!offerId) {
      return {
        success: false,
        error: 'Offer ID is required'
      };
    }
    
    const updateData: any = {};
    if (imageUrls.imageUrl) updateData.image_url = imageUrls.imageUrl;
    if (imageUrls.businessLogoUrl) updateData.business_logo_url = imageUrls.businessLogoUrl;
    if (imageUrls.barcodeUrl) updateData.barcode_url = imageUrls.barcodeUrl;
    
    if (Object.keys(updateData).length === 0) {
      return {
        success: false,
        error: 'No image URLs provided to update'
      };
    }
    
    console.log('💾 Updating offer image URLs:', { offerId, updateData });
    
    const { error: updateError } = await supabase
      .from('offers')
      .update(updateData)
      .eq('id', offerId);
    
    if (updateError) {
      console.error('❌ Database update failed:', updateError);
      return {
        success: false,
        error: `Failed to save image URLs: ${updateError.message}`
      };
    }
    
    console.log('✅ Image URLs saved to database');
    
    return {
      success: true
    };
  } catch (err: any) {
    console.error('❌ Update exception:', err);
    return {
      success: false,
      error: `Update failed: ${err.message}`
    };
  }
};

/**
 * Diagnostic function
 */
export const diagnoseStorageIssue = async (): Promise<string> => {
  const diagnostics: string[] = [];
  
  try {
    diagnostics.push('\n=== WORKAROUND STATUS ===');
    diagnostics.push('✅ Using temp_image_uploads table (base64 storage)');
    diagnostics.push('✅ Bypasses storage.objects RLS issues entirely');
    diagnostics.push('⚠️ Images stored as base64 - not optimized for production');
    
    return diagnostics.join('\n');
  } catch (err: any) {
    return `❌ Diagnostic failed: ${err.message}`;
  }
};

// Remove unused functions that relied on storage.objects
export const getAllowedUploadPath = async () => {
  return {
    allowed: false,
    error: 'This function is deprecated - using temp_image_uploads workaround'
  };
};